import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { LocationSearchRequest } from '../../../shared/location/model/location-search-request.model';
import { SearchResult } from '../../../shared/search-result.model';
import { ColumnFilterComponent, FilterType } from '../../../shared/column-filter/column-filter.component';
import { finalize } from 'rxjs/internal/operators/finalize';
import { HttpResponse } from '@angular/common/http';
import { SORT_PREDICATE_NONE } from '../../../germplasm-manager/germplasm-search-resolve-paging-params';
import { MatchType } from '../../../shared/column-filter/column-filter-text-with-match-options-component';
import { JhiEventManager, JhiLanguageService } from '../../../shared/core-utils';
import { AlertService } from '../../../shared/alert/alert.service';
import { LocationService } from '../../../shared/location/service/location.service';
import { Location } from '../../../shared/location/model/location';
import { LocationType } from '../../../shared/location/model/location-type';
import { ParamContext } from '../../../shared/service/param.context';
import { ProgramFavoriteRequest } from '../../../shared/program/model/program-favorite-add-request.model';
import { ProgramFavoriteTypeEnum } from '../../../shared/program/model/program-favorite-type.enum';
import { ProgramService } from '../../../shared/program/service/program.service';
import { ProgramFavorite } from '../../../shared/program/model/program-favorite.model';
import { ColumnFilterRadioButtonOption } from '../../../shared/column-filter/column-filter-radio-component';
import { ColumnFilterTransitionEventModel } from '../../../shared/column-filter/column-filter-transition-event.model';
import { DropdownOptionData } from '../../../shared/column-filter/column-filter-dropdown-option-data';
import { SubscriptionsManager } from '../../../shared/util/subscriptions-manager';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'jhi-locations-pane',
    templateUrl: 'locations-pane.component.html'
})
export class LocationsPaneComponent implements OnInit, OnDestroy {

    static readonly COLUMN_FILTER_EVENT_NAME = 'searchColumnFiltersChanged';

    @ViewChild('fileUpload', { static: true })
        fileUpload: ElementRef;

    COLUMN_FILTER_EVENT_NAME = LocationsPaneComponent.COLUMN_FILTER_EVENT_NAME;

    itemsPerPage = 20;

    ColumnLabels = ColumnLabels;

    locations: Location[];
    searchRequest: LocationSearchRequest;
    subscriptionsManager: SubscriptionsManager = new SubscriptionsManager();
    resultSearch: SearchResult;

    currentSearch: string;
    totalItems: number;
    page: number;
    previousPage: number;
    predicate: any;
    reverse: boolean;

    isLoading: boolean;

    locationFilters: any;
    isProgramFavoriteFilterApplied: boolean;
    programFavoriteFilterKey = 'filterFavoriteProgramUUID';

    isTogglingFavoriteProgram: boolean;

    constructor(private jhiLanguageService: JhiLanguageService,
                private eventManager: JhiEventManager,
                private locationService: LocationService,
                private alertService: AlertService,
                private context: ParamContext,
                private programService: ProgramService,
                private translateService: TranslateService
    ) {
        this.page = 1;
        this.totalItems = 0;
        this.currentSearch = '';
        this.predicate = ColumnLabels.LOCATION_NAME;
        this.isProgramFavoriteFilterApplied = false;
        this.reverse = true;
        this.resultSearch = new SearchResult('');
        this.searchRequest = new LocationSearchRequest();
        this.searchRequest.filterFavoriteProgramUUID = true;
        this.searchRequest.favoriteProgramUUID = this.context.programUUID;
    }

    ngOnDestroy(): void {
        this.subscriptionsManager.unsubscribeAll();
    }

    ngOnInit(): void {
        this.filters = this.getInitialFilters();
        this.eventManager.broadcast({ name: 'reloadFilters', content: { filters: this.filters, request: this.request } });
        this.registerColumnFiltersChanged();
        this.loadAll(this.request);
    }

    get request() {
        return this.searchRequest;
    }

    set request(request: LocationSearchRequest) {
        this.searchRequest = request;
    }

    get filters() {
        return this.locationFilters;
    }

    set filters(filters) {
        this.locationFilters = filters;
    }

    loadAll(request: LocationSearchRequest) {
        this.isLoading = true;
        this.locationService.searchLocations(request, false,
            {
                page: this.page - 1,
                size: this.itemsPerPage,
                sort: this.getSort()
            }
        ).pipe(finalize(() => {
            this.isLoading = false;
        })).subscribe(
            (res: HttpResponse<Location[]>) => this.onSuccess(res.body, res.headers)
        );
    }

    loadPage(page: number) {
        if (page !== this.previousPage) {
            this.previousPage = page;
            this.transition();
        }
    }

    transition() {
        this.loadAll(this.request);
    }

    private getSort() {
        if (this.predicate === SORT_PREDICATE_NONE) {
            return '';
        }
        return [this.predicate + ',' + (this.reverse ? 'asc' : 'desc')];
    }

    private clearSort() {
        this.predicate = SORT_PREDICATE_NONE;
        this.reverse = false;
    }

    onClearSort($event) {
        $event.preventDefault();
        this.clearSort();
        this.transition();
    }

    trackId(index: number, item: Location) {
        return item.id;
    }

    private registerColumnFiltersChanged() {
        this.subscriptionsManager.add = this.eventManager.subscribe(LocationsPaneComponent.COLUMN_FILTER_EVENT_NAME, (event: ColumnFilterTransitionEventModel) => {
            this.resetTable();

            const programFavoriteFilter = event.filtersApplied.filter((filter: any) =>
                filter.key === this.programFavoriteFilterKey && (filter.value !== undefined));
            this.isProgramFavoriteFilterApplied = (programFavoriteFilter.length === 1) ? true : false;
        });
    }

    resetTable() {
        this.page = 1;
        this.previousPage = 1;
        this.loadAll(this.request);
    }

    private getInitialFilters() {
        const me = this;

        return [
            { key: 'locationNameFilter', name: 'column-filter.location.name', placeholder: 'column-filter.search.text.placeholder', type: FilterType.TEXT_WITH_MATCH_OPTIONS,
                matchType: MatchType.STARTSWITH, default: true
            },
            { key: 'abbreviations', name: 'column-filter.abbreviation', placeholder: 'column-filter.match.text.placeholder', type: FilterType.TEXT,
                transform(req) {
                    req[this.key] = [this.value];
                }
            },
            { key: 'locationDbIds', name: 'column-filter.location.id', type: FilterType.LIST },
            { key: 'countryName', name: 'column-filter.country.name', placeholder: 'column-filter.contains.text.placeholder', type: FilterType.TEXT },
            { key: 'parentLocName', name: 'column-filter.parent.location.name', placeholder: 'column-filter.contains.text.placeholder', type: FilterType.TEXT },
            { key: 'latitude', name: 'column-filter.latitude', type: FilterType.NUMBER_RANGE,
                fromKey: 'latitudeFrom',
                toKey: 'latitudeTo',
                transform(req) {
                    ColumnFilterComponent.transformNumberRangeFilter(this, req, this.fromKey, this.toKey);
                },
                reset(req) {
                    ColumnFilterComponent.resetRangeFilter(this, req, this.fromKey, this.toKey);
                },
                reload(req) {
                    this.from = req[this.fromKey];
                    this.to = req[this.toKey];
                }
            },
            { key: 'longitude', name: 'column-filter.longitude', type: FilterType.NUMBER_RANGE,
                fromKey: 'longitudeFrom',
                toKey: 'longitudeTo',
                transform(req) {
                    ColumnFilterComponent.transformNumberRangeFilter(this, req, this.fromKey, this.toKey);
                },
                reset(req) {
                    ColumnFilterComponent.resetRangeFilter(this, req, this.fromKey, this.toKey);
                },
                reload(req) {
                    this.from = req[this.fromKey];
                    this.to = req[this.toKey];
                }
            },
            { key: 'altitude', name: 'column-filter.altitude', type: FilterType.NUMBER_RANGE,
                fromKey: 'altitudeMin',
                toKey: 'altitudeMax',
                transform(req) {
                    ColumnFilterComponent.transformNumberRangeFilter(this, req, this.fromKey, this.toKey);
                },
                reset(req) {
                    ColumnFilterComponent.resetRangeFilter(this, req, this.fromKey, this.toKey);
                },
                reload(req) {
                    this.from = req[this.fromKey];
                    this.to = req[this.toKey];
                }
            },
            { key: 'locationTypeIds', name: 'column-filter.location.type', type: FilterType.DROPDOWN, values: this.getLocationTypesOptions(), multipleSelect: true,
                transform(req) {
                    ColumnFilterComponent.transformDropdownFilter(this, req);
                },
                reset(req) {
                    ColumnFilterComponent.resetDropdownFilter(this, req);
                },
            },
            { key: this.programFavoriteFilterKey, name: 'column-filter.program.favorite', type: FilterType.RADIOBUTTON, options: this.getProgramFavoriteFilterOptions(),
                transform(req) {
                    req[this.key] = this.value;
                    req['favoriteProgramUUID'] = (this.value) ? me.context.programUUID : null;
                },
                default: true
            }
        ];
    }

    private getLocationTypesOptions(): Promise<DropdownOptionData[]> {
        return this.locationService.getLocationTypes(false).toPromise().then((locationTypes: LocationType[]) => {
            return locationTypes.map((locationType: LocationType) => {
                return { id: locationType.id.toString(),
                    text: locationType.name
                };
            });
        });
    }

    private getProgramFavoriteFilterOptions(): Promise<ColumnFilterRadioButtonOption[]> {
        return new Promise<ColumnFilterRadioButtonOption[]>((resolve, reject) => {
            resolve([new ColumnFilterRadioButtonOption(true, 'yes'),
                new ColumnFilterRadioButtonOption(false, 'no')]);
        });
    }

    private onSuccess(data: Location[], headers) {
        this.totalItems = headers.get('X-Total-Count');
        this.locations = data;
    }

    isProgramFavorite(location: Location): boolean {
        const programFavorite: ProgramFavorite = this.getProgramFavorite(location);
        return programFavorite && this.context.programUUID === programFavorite.programUUID;
    }

    private getProgramFavorite(location: Location): ProgramFavorite {
        // We get the first item because for now on the search location response only returns the program favorite of the current program
        return (location.programFavorites && location.programFavorites.length === 1) ? location.programFavorites[0] : null;
    }

    toggleProgramFavorite(location: Location) {
        // Prevent multiple click while a program favorite is being added/removed
        if (this.isTogglingFavoriteProgram) {
            return;
        }

        this.isTogglingFavoriteProgram = true;

        if (this.isProgramFavorite(location)) {
            this.programService.removeProgramFavorite([location.programFavorites[0].programFavoriteId])
                .pipe(finalize(() => {
                    this.isTogglingFavoriteProgram = false;
                })).subscribe(
                    (res: void) => this.onToggleProgramFavoriteSuccess(location, false, null)
                );
        } else {
            this.programService.addProgramFavorite(
                new ProgramFavoriteRequest(ProgramFavoriteTypeEnum.LOCATION, [location.id]))
                .pipe(finalize(() => {
                    this.isTogglingFavoriteProgram = false;
                })).subscribe(
                    (res: HttpResponse<ProgramFavorite[]>) => this.onToggleProgramFavoriteSuccess(location, true, res.body[0])
                );
        }
    }

    private onToggleProgramFavoriteSuccess(location: Location, wasAdded: boolean, programFavorite: ProgramFavorite) {
        if (wasAdded) {
            location.programFavorites = [new ProgramFavorite(programFavorite.programFavoriteId, programFavorite.entityType, programFavorite.entityId, programFavorite.programUUID)];
        } else {
            location.programFavorites = null;
        }

        if (this.isProgramFavoriteFilterApplied) {
            this.resetTable();
        }
    }

}

export enum ColumnLabels {
    'LOCATION_NAME' = 'LOCATION_NAME',
    'ABBREVIATION' = 'ABBREVIATION',
    'LOCATION_ID' = 'LOCATION_ID',
    'COUNTRY' = 'COUNTRY',
    'PARENT_LOC' = 'PARENT_LOC',
    'LATITUDE' = 'LATITUDE',
    'LONGITUDE' = 'LONGITUDE',
    'ALTITUDE' = 'ALTITUDE',
    'TYPE' = 'TYPE',
    'FAVORITE_PROGRAM_UUID' = 'FAVORITE_PROGRAM_UUID',
}
